import React, { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Button, ModalShell, ProgressBar } from "./atoms";
import { useLifeAreas } from "./config";
import { isNetworkError, postCheckIn, queueCheckIn } from "./ringsApi";
import { fs, ink, T, withAlpha } from "./tokens";
import { DimensionKey, RingInsight, RingSnapshot } from "./types";

type Props = {
  snapshot: RingSnapshot;
  initialKey: DimensionKey | null;
  onClose: () => void;
  onSaved: (snapshot: RingSnapshot, insights: RingInsight[]) => void;
  onViewSteps: () => void;
};

const THUMB_PX = 20;

const Footer: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>{children}</div>
);

// 0 area → 1 question card → 2 slider → 3 note + mood → 4 confirm → 5 done
const CheckInModal: React.FC<Props> = ({ snapshot, initialKey, onClose, onSaved, onViewSteps }) => {
  const L = useLifeAreas();
  const { scale, limits } = L.config;
  const snapToStep = (n: number) => Math.round(n / scale.step) * scale.step;
  const midpoint = snapToStep((scale.min + scale.max) / 2);
  const [step, setStep] = useState(initialKey ? 1 : 0);
  const [key, setKey] = useState<DimensionKey | null>(initialKey);
  const dim = snapshot.dimensions.find((d) => d.key === key) || null;
  const area = key ? L.area(key) : null;
  const [score, setScore] = useState<number>(snapToStep(L.display(dim?.score ?? null) ?? midpoint));
  const [reflection, setReflection] = useState("");
  const [mood, setMoodState] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [insight, setInsight] = useState<RingInsight | null>(null);
  const [queued, setQueued] = useState(false);

  const availability = useMemo(() => {
    const now = Date.now();
    const out: Partial<Record<DimensionKey, Date>> = {};
    for (const d of snapshot.dimensions) {
      if (!d.lastUpdatedAt) continue;
      const next = new Date(d.lastUpdatedAt).getTime() + limits.cooldownHours * 3600 * 1000;
      if (next > now) out[d.key] = new Date(next);
    }
    return out;
  }, [snapshot, limits.cooldownHours]);

  const save = async () => {
    if (!key) return;
    setSaving(true);
    // mood and note are saved with the check-in on the server (synced everywhere)
    const entry = {
      dimensionKey: key,
      score: L.toStored(score),
      reflection: reflection.trim(),
      mood: mood ?? "",
    };
    try {
      const { snapshot: next, insights } = await postCheckIn([entry]);
      onSaved(next, insights);
      setInsight(insights.find((i) => i.dimensionKey === key) || insights[0] || null);
      setStep(5);
    } catch (err: any) {
      if (isNetworkError(err)) {
        queueCheckIn(entry);
        setQueued(true);
        setStep(5);
      } else {
        toast.error(err?.response?.data?.message || "Could not save your check-in");
      }
    } finally {
      setSaving(false);
    }
  };

  const title =
    step === 0
      ? "What area would you like to update?"
      : step === 1
        ? `${area?.emoji} ${area?.label}`
        : step === 2
          ? "How would you rate it?"
          : step === 3
            ? "Anything you'd like to note?"
            : step === 4
              ? "Ready to save?"
              : queued
                ? "Saved offline"
                : "GPS updated!";

  const band = L.bandFor(score);
  const last = L.display(dim?.score ?? null); // the score before this check-in (ghost marker)
  const pct = (v: number) => ((v - scale.min) / (scale.max - scale.min)) * 100;
  const markerLeft = (v: number) => `calc(${pct(v)}% + ${(0.5 - pct(v) / 100) * THUMB_PX}px)`;
  const tint = area?.color ?? T.color.primary;

  return (
    <ModalShell ariaLabel="Update Life Areas" title={title} onClose={onClose} focusKey={step}>
      {step < 5 && (
        <div style={{ marginBottom: 16 }}>
          <ProgressBar value={(step / 5) * 100} color={tint} height={4} label="Check-in progress" />
        </div>
      )}

      {/* 0: area */}
      {step === 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {L.areas.map((a) => {
            const k = a.key;
            const d = snapshot.dimensions.find((x) => x.key === k);
            if (!d) return null;
            const wait = availability[k];
            const disabled = !d.isActive || Boolean(wait);
            return (
              <button
                key={k}
                type="button"
                disabled={disabled}
                className="lg2-row"
                onClick={() => {
                  setKey(k);
                  setScore(snapToStep(L.display(d.score) ?? midpoint));
                  setStep(1);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  textAlign: "left",
                  padding: "10px 12px",
                  background: T.color.bg.card,
                  border: `1px solid ${disabled ? T.color.bg.overlay : withAlpha(a.color, 0.45)}`,
                  borderRadius: T.radius.lg,
                  color: T.color.text.primary,
                  opacity: disabled ? 0.45 : 1,
                  cursor: disabled ? "not-allowed" : "pointer",
                }}
              >
                <span style={{ fontSize: 20 }} aria-hidden="true">
                  {a.emoji}
                </span>
                <span>
                  <span style={{ ...fs("sm", 600), display: "block" }}>{a.label}</span>
                  {wait && (
                    <span style={{ ...fs("xs"), display: "block", color: T.color.text.secondary }}>
                      again at {wait.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* 1: question card */}
      {step === 1 && area && dim && (
        <div>
          <div
            style={{
              padding: 20,
              textAlign: "center",
              background: withAlpha(area.color, 0.1),
              border: `1px solid ${withAlpha(area.color, 0.35)}`,
              borderRadius: T.radius.xl,
            }}
          >
            <div style={{ fontSize: 40 }} aria-hidden="true">
              {area.emoji}
            </div>
            <p style={{ ...fs("md", 500), margin: "10px 0 0" }}>{dim.question}</p>
            <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 12 }} aria-hidden="true">
              {Array.from({ length: dim.questionTotal }, (_, i) => (
                <span
                  key={i}
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: T.radius.full,
                    background: i === dim.questionIndex ? area.color : withAlpha(area.color, 0.3),
                  }}
                />
              ))}
            </div>
          </div>
          <Footer>
            {!initialKey && (
              <Button variant="ghost" onClick={() => setStep(0)}>
                Back
              </Button>
            )}
            <Button onClick={() => setStep(2)}>Next</Button>
          </Footer>
        </div>
      )}

      {/* 2: slider with the ghost marker */}
      {step === 2 && area && (
        <div>
          <div aria-live="polite" style={{ textAlign: "center", margin: "0 0 16px" }}>
            <div style={{ ...fs("xxl", 700), color: band ? ink(band.color) : T.color.text.primary }}>
              {band?.label}
            </div>
            <div style={{ ...fs("sm"), color: T.color.text.secondary }}>{score}</div>
          </div>
          <div style={{ position: "relative", paddingTop: 22 }}>
            {last != null && (
              <span
                aria-hidden="true"
                style={{
                  ...fs("xs"),
                  position: "absolute",
                  top: 0,
                  left: markerLeft(last),
                  transform: "translateX(-50%)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  color: T.color.text.secondary,
                  pointerEvents: "none",
                }}
              >
                Last: {last}
                <span style={{ width: 2, height: 8, marginTop: 2, borderRadius: 2, background: T.color.text.muted }} />
              </span>
            )}
            <input
              type="range"
              min={scale.min}
              max={scale.max}
              step={scale.step}
              value={score}
              onChange={(e) => {
                setScore(Number(e.target.value));
                navigator.vibrate?.(8); // haptic tick where supported
              }}
              aria-label={`${area.label} score`}
              aria-valuetext={`${score} out of ${scale.max}, ${band?.label ?? ""}`}
              className="lg2-range"
              style={{ "--lg2-fill": area.color, "--lg2-pct": `${pct(score)}%` } as React.CSSProperties}
            />
          </div>
          <div style={{ ...fs("xs"), display: "flex", justifyContent: "space-between", color: T.color.text.secondary }}>
            <span>{scale.min}</span>
            <span>{scale.max}</span>
          </div>
          <Footer>
            <Button variant="ghost" onClick={() => setStep(1)}>
              Back
            </Button>
            <Button onClick={() => setStep(3)}>Next</Button>
          </Footer>
        </div>
      )}

      {/* 3: note + mood */}
      {step === 3 && (
        <div>
          <textarea
            value={reflection}
            onChange={(e) => setReflection(e.target.value.slice(0, limits.reflectionMax))}
            placeholder="Optional reflection…"
            rows={4}
            style={{
              ...fs("sm"),
              width: "100%",
              background: T.color.bg.card,
              color: T.color.text.primary,
              border: `1px solid ${T.color.bg.overlay}`,
              borderRadius: T.radius.lg,
              padding: 12,
              resize: "vertical",
            }}
          />
          <p style={{ ...fs("xs"), margin: "4px 0 0", textAlign: "right", color: T.color.text.secondary }}>
            {reflection.length}/{limits.reflectionMax}
          </p>
          <p style={{ ...fs("xs", 600), margin: "10px 0 8px", color: T.color.text.secondary }}>
            How are you feeling? (optional)
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {L.config.moods.map((m) => {
              const on = mood === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setMoodState(on ? null : m.id)}
                  style={{
                    ...fs("xs", 500),
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "5px 12px",
                    borderRadius: T.radius.full,
                    color: T.color.text.primary,
                    background: on ? withAlpha(T.color.primary, 0.3) : T.color.bg.card,
                    border: `1px solid ${on ? T.color.primary : T.color.bg.overlay}`,
                    cursor: "pointer",
                  }}
                >
                  <span aria-hidden="true">{m.emoji}</span>
                  {m.label}
                </button>
              );
            })}
          </div>
          <Footer>
            <Button variant="ghost" onClick={() => setStep(2)}>
              Back
            </Button>
            <Button onClick={() => setStep(4)}>Next</Button>
          </Footer>
        </div>
      )}

      {/* 4: confirm */}
      {step === 4 && area && (
        <div>
          <div
            style={{
              padding: 16,
              textAlign: "center",
              background: T.color.bg.card,
              border: `1px solid ${withAlpha(area.color, 0.4)}`,
              borderRadius: T.radius.xl,
            }}
          >
            <div style={{ fontSize: 32 }} aria-hidden="true">
              {area.emoji}
            </div>
            <p style={{ ...fs("sm"), margin: "4px 0 0", color: T.color.text.secondary }}>{area.label}</p>
            <p style={{ ...fs("xl", 700), margin: 0, color: band ? ink(band.color) : undefined }}>
              {band?.label}
            </p>
            <p style={{ ...fs("sm"), margin: 0, color: T.color.text.secondary }}>
              {score} of {scale.max}
            </p>
            {L.mood(mood) && (
              <p style={{ ...fs("sm"), margin: "8px 0 0", color: T.color.text.secondary }}>
                {L.mood(mood)?.emoji} {L.mood(mood)?.label}
              </p>
            )}
            {reflection.trim() && (
              <p style={{ ...fs("xs"), margin: "8px 0 0", fontStyle: "italic", color: T.color.text.secondary }}>
                “{reflection.trim()}”
              </p>
            )}
          </div>
          <Footer>
            <Button variant="ghost" onClick={() => setStep(3)}>
              Back
            </Button>
            <Button onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Confirm & save"}
            </Button>
          </Footer>
        </div>
      )}

      {/* 5: done */}
      {step === 5 && area && (
        <div style={{ textAlign: "center" }} aria-live="polite">
          <div style={{ ...fs("xxl", 700), color: band ? ink(band.color) : T.color.text.primary }}>
            {band?.label}
          </div>
          <p style={{ ...fs("sm"), margin: "4px 0 0", color: T.color.text.secondary }}>
            {area.label} ({score}) {queued ? "will sync when you're back online" : "updated"}
          </p>
          {insight && (
            <p
              style={{
                ...fs("sm"),
                margin: "16px 0 0",
                padding: 12,
                background: T.color.bg.card,
                borderRadius: T.radius.lg,
              }}
            >
              💡 {insight.headline}
            </p>
          )}
          <Footer>
            <Button variant="ghost" onClick={onClose}>
              Done
            </Button>
            {!queued && (
              <Button
                onClick={() => {
                  onClose();
                  onViewSteps();
                }}
              >
                See next steps
              </Button>
            )}
          </Footer>
        </div>
      )}
    </ModalShell>
  );
};

export default CheckInModal;
